import React from 'react';
import { ModalV2, ModalTitle, MultiToggle, ToggleSwitchButton } from '../../../../../core/components';
import { ExperimentConfiguration, TissueCharacteristicsProps, IAnatomicalModel } from '../../../models';
import anatomicalModelFeaturesConstants from '../../../constants/anatomical-model-constants';
import { MotionSection } from './motion-section';
import { ContrastSection } from './contrast-section';
import { TissueMagneticPropertiesSection } from './tissue-magnetic-properties-section';
import { isAnatomicalModelFeatureEnabled } from '../../../utils/is-anatomical-model-feature-enabled';
import { getSelectedValueFromSimulatorConfig } from '../../../utils/get-selected-value-from-simulator-config';
import simulatorConfig, {
  brainCondition as brainConditionConstants,
  breathingMode as breathingModeConstants,
} from '../../../constants/simulator-config-constants';
import { ModalSubSection } from '../modal-sub-section';
import { ModalSectionContainer } from '../modal-section-container';
import { HeartConditionSection } from './heart-condition-subsection';
import { CardiacPhaseSubSection } from './cardiac-phase-subsection';
import { brainAnatomicalModels } from '../../../constants/simulator-constants';
import { AppConfig } from '../../../../../core/config';
import { AppEnvironments } from '../../../../../core/constants';

export interface ModalProps {
  config: ExperimentConfiguration[];
  motionParameters: ExperimentConfiguration[];
  contrastParameters: ExperimentConfiguration[];
  tissueCharacteristics: TissueCharacteristicsProps[];
  anatomicalModel: IAnatomicalModel;
  onClose: VoidFunction;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  onSubmit: (values: any) => void;
  onSubmitCell: (type: string, t1: number, t2: number) => void;
  updatePatientSettings: (action: string, value: string, configurationName: string) => void;
  resetTissueCharacteristics: () => void;
  onDefaultCardiacPhaseChange: (values: { default_cardiac_phase_value: string }) => void;
}

export const PatientSettingsModal = ({
  onClose,
  onSubmit,
  onSubmitCell,
  motionParameters,
  contrastParameters,
  tissueCharacteristics,
  config,
  updatePatientSettings,
  resetTissueCharacteristics,
  anatomicalModel,
  onDefaultCardiacPhaseChange,
}: ModalProps): React.ReactElement => {
  const onBreathingModeSelectHandler = (breathingMode: string) => {
    updatePatientSettings('true', breathingMode, simulatorConfig.BREATHING_MODE);
  };
  const showBreathingMode = isAnatomicalModelFeatureEnabled(
    anatomicalModel,
    anatomicalModelFeaturesConstants.breathingMode
  );
  const breathingMode = getSelectedValueFromSimulatorConfig(config, simulatorConfig.BREATHING_MODE);
  const contrastActiveMode = getSelectedValueFromSimulatorConfig(config, simulatorConfig.ACTIVATE_CONTRAST);

  const showHeartConditionToggle = isAnatomicalModelFeatureEnabled(
    anatomicalModel,
    anatomicalModelFeaturesConstants.heartCondition
  );
  const defaultCardiacPhaseParameters = getSelectedValueFromSimulatorConfig(
    config,
    simulatorConfig.DEFAULT_CARDIAC_PHASE
  );

  const defaultConfigurationSettingsSelected =
    getSelectedValueFromSimulatorConfig(config, simulatorConfig.OVERWRITE_SIMULATION_SETTINGS) === '1';

  const showDefaultCardiacPhase = isAnatomicalModelFeatureEnabled(
    anatomicalModel,
    anatomicalModelFeaturesConstants.defaultCardiacPhase
  );

  const onBrainConditionSelectHandler = (brainCondition: string) => {
    updatePatientSettings('true', brainCondition, simulatorConfig.BRAIN_CONDITION);
  };

  const onContrastImagingSelectHandler = (contrastImaging: string) => {
    updatePatientSettings('true', contrastImaging, simulatorConfig.ACTIVATE_CONTRAST);
  };

  const showBrainConditionToggle = isAnatomicalModelFeatureEnabled(
    anatomicalModel,
    anatomicalModelFeaturesConstants.brainCondition
  );
  const brainConditionSelectedValue = getSelectedValueFromSimulatorConfig(config, simulatorConfig.BRAIN_CONDITION);

  const showAdvancedSection =
    showBreathingMode || showBrainConditionToggle || showHeartConditionToggle || showDefaultCardiacPhase;

  const isBrainAnatomicalModel = brainAnatomicalModels.map((model) => model.id).includes(anatomicalModel?.id);

  return (
    <ModalV2
      closeOnOutsideClick
      withCloseButton
      onClose={onClose}
      header={<ModalTitle>Patient Settings</ModalTitle>}
      style={{ width: '1024px' }}
    >
      <>
        <MotionSection
          config={config}
          motionParameters={motionParameters}
          onSubmit={onSubmit}
          setMotionType={updatePatientSettings}
        />

        {anatomicalModel?.features?.some(
          (f) => f.name === anatomicalModelFeaturesConstants.tissueMagneticProperties
        ) && (
          <TissueMagneticPropertiesSection
            config={config}
            onSubmitCell={onSubmitCell}
            resetTissueCharacteristics={resetTissueCharacteristics}
            tissueCharacteristics={tissueCharacteristics}
            setUseCustomTissueMagneticProperties={updatePatientSettings}
          />
        )}

        {showAdvancedSection && (
          <ModalSectionContainer title="Advanced">
            {showBreathingMode && (
              <ModalSubSection title="Breathing mode">
                <MultiToggle
                  items={[
                    {
                      id: breathingModeConstants.BREATHHOLD_FULL_EXHALE,
                      label: 'Breathhold at Exhale (default)',
                    },
                    { id: breathingModeConstants.BREATHHOLD_FULL_INHALE, label: 'Breathhold at Inhale' },
                    { id: breathingModeConstants.FREE_BREATHING, label: 'Free-breathing' },
                  ]}
                  onSelect={(selectedBreathingMode) => onBreathingModeSelectHandler(selectedBreathingMode)}
                  selectedItemKey={breathingMode}
                />
              </ModalSubSection>
            )}

            {showHeartConditionToggle && (
              <HeartConditionSection config={config} updatePatientSettings={updatePatientSettings} />
            )}

            {showBrainConditionToggle && (
              <ModalSubSection title="Brain condition">
                <MultiToggle
                  items={[
                    { id: brainConditionConstants.HEALTHY, label: 'Healthy' },
                    { id: brainConditionConstants.MILD_MS_LESIONS, label: 'Mild MS' },
                    { id: brainConditionConstants.MODERATE_MS_LESIONS, label: 'Moderate MS' },
                    { id: brainConditionConstants.SEVERE_MS_LESIONS, label: 'Severe MS' },
                    { id: brainConditionConstants.TUMOR, label: 'Tumor' },
                  ]}
                  onSelect={(brainConditionValue) => onBrainConditionSelectHandler(brainConditionValue)}
                  selectedItemKey={brainConditionSelectedValue}
                />
              </ModalSubSection>
            )}
            {showDefaultCardiacPhase && (
              <CardiacPhaseSubSection
                defaultConfigurationSettingsSelected={defaultConfigurationSettingsSelected}
                onDefaultCardiacPhaseChange={onDefaultCardiacPhaseChange}
                defaultCardiacPhaseParameters={defaultCardiacPhaseParameters}
              />
            )}
          </ModalSectionContainer>
        )}

        {isBrainAnatomicalModel && (
          <ModalSectionContainer title="Contrast enhanced imaging">
            <div className="mt-3 mb-3">
              <ToggleSwitchButton
                checked={contrastActiveMode === 'true'}
                id="beta_feature_toggle"
                onClick={() => {
                  onContrastImagingSelectHandler(contrastActiveMode === 'true' ? 'false' : 'true');
                }}
              >
                Enable contrast enhanced imaging
              </ToggleSwitchButton>
              {contrastActiveMode === 'true' && !!contrastParameters.length && (
                <ContrastSection config={config} contrastParameters={contrastParameters} onSubmit={onSubmit} />
              )}
            </div>
          </ModalSectionContainer>
        )}
      </>
    </ModalV2>
  );
};
